'use client';

import { useTranslations } from 'next-intl';
import { ReactNode, createContext, useCallback, useState } from 'react';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './delivery-zones-modal.scss';

type Props = ModalProps & {
    children?: ReactNode;
};

export type DeliveryZonesModalContextValue = {
    showAllZones: boolean;
    setShowAllZones: (value: boolean) => void;
    selectedZoneKey: string | null;
    setSelectedZoneKey: (value: string | null) => void;
    toggleSelectedZone: (zoneKey: string) => void;
};

export const DeliveryZonesModalContext = createContext<DeliveryZonesModalContextValue | null>(null);

export const DeliveryZonesModalProvider = ({ children }: { children?: ReactNode }) => {
    const [showAllZones, setShowAllZones] = useState(false);
    const [selectedZoneKey, setSelectedZoneKey] = useState<string | null>(null);

    const handleSetShowAllZones = useCallback((value: boolean) => {
        setShowAllZones(value);
        if (!value) {
            setSelectedZoneKey(null);
        }
    }, []);

    const toggleSelectedZone = useCallback((zoneKey: string) => {
        setSelectedZoneKey((current) => (current === zoneKey ? null : zoneKey));
    }, []);

    return (
        <DeliveryZonesModalContext.Provider
            value={{
                showAllZones,
                setShowAllZones: handleSetShowAllZones,
                selectedZoneKey,
                setSelectedZoneKey,
                toggleSelectedZone,
            }}
        >
            {children}
        </DeliveryZonesModalContext.Provider>
    );
};

export const DeliveryZonesModalWrapper = ({ children, ...props }: Props) => {
    return (
        <DeliveryZonesModalProvider>
            <Modal
                {...props}
                wrapperProps={{
                    containerProps: { className: 'delivery-zones-modal__wrapper' },
                    scrollerProps: { className: 'delivery-zones-modal__scroller' },
                    'data-testid': 'delivery-zones-modal',
                }}
            >
                {children}
            </Modal>
        </DeliveryZonesModalProvider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export { DeliveryZonesModalProvider as Provider };

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const DeliveryZonesModalTitle = () => {
    const t = useTranslations();
    return <>{t('DELIVERY_ZONES_MODAL.TITLE')}</>;
};

export { DeliveryZonesModalWrapper as DeliveryZonesModal };

export { DeliveryZonesModalWrapper as Wrapper, DeliveryZonesModalTitle as Title };
